import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Point
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import axios, { CancelToken } from 'axios';

import { useAuth } from 'hooks/use-auth';
import {
  Status, parseStatuses,
  StatusLog, parseStatusLogs, applyStatusProps
} from 'models';
import { Time } from 'utils';

ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);


interface Props {
  companyId: number;
  factoryId: number;
  equipmentId: string;
  period?: number;
  numPlots?: number;
  interval?: number;
}

export function EquipmentHistoryChart({
  companyId,
  factoryId,
  equipmentId,
  period = 3600 * 24,
  numPlots = 50,
  interval = 60
}: Props) {
  const auth = useAuth();

  const [periodMsec, setPeriodMsec] = useState(Time.secToMsec(period));
  const [intervalMsec, setIntervalMsec] = useState(Time.secToMsec(interval));
  const [granularityMsec, setGranularityMsec] = useState(Math.floor(periodMsec / numPlots));
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [statusLogs, setStatusLogs] = useState<StatusLog[] | null>(null);
  const [lastDataTimestamp, setLastDataTimestamp] = useState<Date>();
  const [data, setData] = useState<any>({labels: [], datasets: []});

  const refChart = useRef<ChartJS<any>>(null);
  const refStatusLogs = useRef(statusLogs);
  const refLastDataTimestamp = useRef(lastDataTimestamp);

  function dateFrom(): Date {
    return Time.dateFromUnixMsec(Time.unixMsec() - periodMsec);
  }

  function dateTo(): Date {
    return new Date();
  }

  async function fetchStatusLogs(cancelToken: CancelToken) {
    axios.get(`/v0/equipments/${equipmentId}/status-logs`, {
      cancelToken: cancelToken,
      headers: auth.authorizationHeader,
      params: {
        company_id: companyId,
        factory_id: factoryId,
        equipment_id: equipmentId,
        time_from: dateFrom(),
        updated_after: refLastDataTimestamp.current || dateFrom()
      }
    }).then(res => {
      const _dateFrom = dateFrom();
      let slogs: StatusLog[] = refStatusLogs.current || [];
      let new_slogs = parseStatusLogs(res.data)
      new_slogs = new_slogs.map(slog => applyStatusProps(slog, statuses));
      slogs = slogs.concat(new_slogs);
      slogs.sort((a, b) => a.timeFrom.getTime() - b.timeFrom.getTime());
      refStatusLogs.current = slogs.filter((val, idx, self) => (
        self.findIndex(item => item.timeFrom.getTime() === val.timeFrom.getTime()) === idx
      )).filter(item => (
        item.timeTo.getTime() >= _dateFrom.getTime()
      ));
      setStatusLogs(refStatusLogs.current);
      refLastDataTimestamp.current = slogs.reduce((cur: Date, item) => (
        item.updatedAt && (item.updatedAt > cur ? item.updatedAt : cur) || cur
      ), refLastDataTimestamp.current || _dateFrom);
      setLastDataTimestamp(refLastDataTimestamp.current);
    }).catch(err => {
      if (err.code != 'ERR_CANCELED') {
        console.error(err);
      }
    });
  }

  useEffect(() => {
    const cancelTokenSrc = axios.CancelToken.source();
    fetchStatusLogs(cancelTokenSrc.token);
    const intervalId = setInterval(fetchStatusLogs , intervalMsec, cancelTokenSrc.token);
    return () => {
      cancelTokenSrc.cancel(`Cancel API request ID: ${intervalId}`);
      clearInterval(intervalId);
    };
  }, [companyId, factoryId, equipmentId, periodMsec, intervalMsec]);

  useEffect(() => {
    setStatusLogs(statusLogs?.map(slog => applyStatusProps(slog, statuses)) || []);
  }, [statuses]);

  useEffect(() => {
    axios.get(`/v0/factories/${factoryId}/statuses`, {
      headers: auth.authorizationHeader,
      params: {
        equipment_id: equipmentId
      }
    }).then(res => {
      setStatuses(parseStatuses(res.data));
    }).catch(err => {
      console.error(err);
    });
  }, [companyId, factoryId, equipmentId, lastDataTimestamp]);

  useEffect(() => {
    setData(makeData(
      statuses,
      statusLogs || [],
      dateFrom(),
      dateTo(),
      granularityMsec
    ));
    refChart.current?.update('none');
  }, [statusLogs]);

  return (
    <div>
      <Line
        ref={refChart}
        options={{
          responsive: true,
          animation: false,
          plugins: {
            title: {
              display: true,
              text: `稼働率 (${Time.msecToHour(periodMsec)}h)`,
            },
            legend: {
              display: false
            }
          },
          scales: {
            x: {
              type: 'time',
              min: dateFrom().getTime(),
              max: dateTo().getTime()
            },
            y: {
              beginAtZero: true,
              max: 100
            }
          }
        }}
        data={data}
        redraw={false}
      />
      {(statusLogs && statusLogs.length < 1) ? (
        `No data`
      ) : (
        <>
        {lastDataTimestamp ? (
          `最終データ日時: ${lastDataTimestamp.toLocaleString()}`
        ) : (
          `Loading ...`
        )}
        </>
      )}
    </div>
  );
}

function makeData(
  statuses: Status[],
  statusLogs: StatusLog[],
  dataTimeFrom: Date,
  dataTimeTo: Date,
  granularityMsec: number
): any {

  let counts: {
    x: number;
    y: number;
  }[] = [];

  const dataTimeFromUnix = Time.round(dataTimeFrom, granularityMsec).getTime();
  const dataTimeToUnix = Time.round(dataTimeTo, granularityMsec).getTime() - granularityMsec;

  for (let from = dataTimeFromUnix; from < dataTimeToUnix; from += granularityMsec) {
    const to = from + granularityMsec;
    const t = statusLogs.filter(slog => (
      slog.statusIsCountTarget &&
      !(to <= slog.timeFrom.getTime() || from >= slog.timeTo.getTime())
    )).reduce((acc, slog) => {
      const overlapBegin = Math.max(slog.timeFrom.getTime(), from);
      const overlapEnd = Math.min(slog.timeTo.getTime(), to);
      return acc + Math.max(0, overlapEnd - overlapBegin);
    }, 0);
    counts.push({
      x: from,
      y: 100.0 * t / granularityMsec
    });
  }

  return {
    labels: [],
    datasets: [{
      label: "",
      data: counts,
      borderColor: 'rgb(55,255,55)',
      backgroundColor: 'rgb(55,255,55)',
      borderWidth: 1,
      borderJoinStyle: 'round',
      cubicInterpolationMode: 'monotone',
      pointRadius: 2
    }]
  };
}

